import { Alert, Button, Checkbox, Modal, Radio, Tag, message } from "antd";
import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "../../config/axios";
import moment from "../../config/moment";
import styles from "./RefundModal.module.scss";

// how the items get back to the shop, see orders.statuses.RefundMethod
export const REFUND_METHOD_SHOP = "shop_drop_off";
export const REFUND_METHOD_PICKUP = "rider_pickup";

// item statuses of a refund, see orders.statuses.OrderItemStatus
const REFUND_STATUS_LABELS = {
  refund_requested: { label: "Refund requested", color: "gold" },
  refund_accepted: { label: "Refund accepted", color: "green" },
  refund_rejected: { label: "Refund rejected", color: "red" },
};

export const refundStatusTag = (status) => {
  const tag = REFUND_STATUS_LABELS[status];
  return tag ? <Tag color={tag.color}>{tag.label}</Tag> : null;
};

// an item can be asked back once it was delivered and its product allows it
export const isRefundableItem = (item) =>
  item.status === "delivered" && !item.is_non_refundable;

export const canRequestRefund = (order) =>
  order.status === "delivered" && order.order_items.some(isRefundableItem);

const DamagedItemsNotice = () => (
  <p className={styles.damaged_notice}>
    Please note that damaged items will be rejected for a refund, both at our
    shop and by our delivery driver. For details, please refer to our{" "}
    <Link to="/terms-and-condition" target="_blank">
      Terms &amp; Conditions
    </Link>
    .
  </p>
);

const RefundModal = ({ order, open, onClose, onRefunded }) => {
  const [step, setStep] = useState("items");
  const [selected, setSelected] = useState([]);
  const [method, setMethod] = useState(REFUND_METHOD_SHOP);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const refundable = order.order_items.filter(isRefundableItem);
  const allSelected = refundable.length > 0 && selected.length === refundable.length;

  const toggleItem = (itemId, checked) =>
    setSelected((current) =>
      checked ? [...current, itemId] : current.filter((id) => id !== itemId)
    );

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(
        `/orders/${order.id}/refund`,
        { item_ids: selected, method },
        { withCredentials: true }
      );
      setResult(res.data);
      setStep("done");
      onRefunded?.(res.data.order);
    } catch (error) {
      message.error(
        error.response?.data?.msg || "Your refund could not be requested. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const footer = {
    items: [
      <Button key="cancel" onClick={onClose}>
        Cancel
      </Button>,
      <Button
        key="next"
        type="primary"
        disabled={selected.length === 0}
        onClick={() => setStep("method")}
      >
        Continue
      </Button>,
    ],
    method: [
      <Button key="back" onClick={() => setStep("items")}>
        Back
      </Button>,
      <Button key="submit" type="primary" loading={submitting} onClick={submit}>
        Request refund
      </Button>,
    ],
    done: [
      <Button key="close" type="primary" onClick={onClose}>
        Close
      </Button>,
    ],
  }[step];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={footer}
      centered
      width={560}
      title={`Refund order #${order.id}`}
      destroyOnClose
    >
      {step === "items" && (
        <div className={styles.items}>
          <p>Please select the items you would like to return.</p>

          {refundable.length > 1 && (
            <Checkbox
              className={styles.select_all}
              checked={allSelected}
              indeterminate={selected.length > 0 && !allSelected}
              onChange={(e) =>
                setSelected(e.target.checked ? refundable.map((item) => item.id) : [])
              }
            >
              Select all items
            </Checkbox>
          )}

          <ul className={styles.item_list}>
            {order.order_items.map((item) => (
              <li key={item.id} className={styles.item}>
                {/* non-refundable products get no checkbox at all */}
                <div className={styles.item_check}>
                  {isRefundableItem(item) && (
                    <Checkbox
                      checked={selected.includes(item.id)}
                      onChange={(e) => toggleItem(item.id, e.target.checked)}
                    />
                  )}
                </div>
                {item.image ? (
                  <img src={item.image} alt="" className={styles.item_image} />
                ) : (
                  <div className={styles.item_image} />
                )}
                <div className={styles.item_text}>
                  <span>{item.title}</span>
                  <small>
                    {item.quantity} × {item.order_time_price} €
                  </small>
                </div>
                <div>
                  {refundStatusTag(item.status) ||
                    (item.is_non_refundable && <Tag>Not refundable</Tag>)}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {step === "method" && (
        <div>
          <p>How would you like to return the selected items?</p>

          <Radio.Group
            className={styles.methods}
            value={method}
            onChange={(e) => setMethod(e.target.value)}
          >
            <Radio value={REFUND_METHOD_SHOP} className={styles.method}>
              <div className={styles.method_title}>
                Bring the items back to our shop yourself <Tag color="green">Recommended</Tag>
              </div>
              <p>
                You are welcome to return the items at our shop at your
                convenience. Our staff will inspect them on the spot and process
                your refund directly. This is the quickest way to receive your
                refund.
              </p>
              <DamagedItemsNotice />
            </Radio>

            <Radio value={REFUND_METHOD_PICKUP} className={styles.method}>
              <div className={styles.method_title}>Collection by our delivery driver</div>
              <p>
                Kindly note that collections are scheduled around our regular
                delivery routes. Our driver will collect the items during their
                next scheduled visit to your area, which may take{" "}
                <strong>at least one week</strong>. Your refund can only be
                processed once the items have reached our shop and been
                inspected. Should you wish to receive your refund sooner, we
                would respectfully recommend returning the items to our shop in
                person.
              </p>
              <DamagedItemsNotice />
            </Radio>
          </Radio.Group>
        </div>
      )}

      {step === "done" && result && (
        <div className={styles.done}>
          <Alert
            type="success"
            showIcon
            message="Your refund request has been received."
            description={
              result.refund.method === REFUND_METHOD_PICKUP ? (
                <>
                  Our driver will collect the items during their next visit to
                  your area
                  {result.next_pickup_date
                    ? `, at the earliest on ${moment(result.next_pickup_date).format("dddd, DD.MM.YYYY")}`
                    : ""}
                  . Please keep the items ready. If nobody is at home when the
                  driver arrives, you will be asked to bring the items to our
                  shop instead.
                </>
              ) : (
                <>
                  Please bring the items, together with your order number{" "}
                  <strong>#{order.id}</strong>, to our shop. Your refund is
                  processed once our staff have checked the items.
                </>
              )
            }
          />
          <DamagedItemsNotice />
        </div>
      )}
    </Modal>
  );
};

export default RefundModal;
